import React, { Dispatch, SetStateAction, useEffect, useRef, useMemo } from 'react';
import { Box, Button, Checkbox, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  FileCopy as FileCopyIcon,
  Done as DoneIcon
} from '@mui/icons-material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { classes } from './styles';
import { arrayQuestion } from './Utility/questionStringToArray';
import { useHighlightOnNewItems } from '../../NewUI/hooks';
import { useScrollToElement } from '../../NewUI/hooks/useScrollToElement';

const SelectableCopyableQuestions = ({
  questions,
  setQuestions,
  maxHeight,
  deleteQuestion,
  hasFeedback,
  newQuestions,
  isLoading
}: {
  questions: arrayQuestion[];
  setQuestions: Dispatch<SetStateAction<arrayQuestion[]>>;
  maxHeight?: string;
  deleteQuestion?: (index: number) => void;
  hasFeedback?: boolean;
  newQuestions?: arrayQuestion[];
  isLoading?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollToHighlighted } = useScrollToElement(containerRef, { offset: 20 });

  // Extract text from newQuestions to match the comparison logic
  const newQuestionTexts = useMemo(
    () => (newQuestions || []).map((question) => question.text),
    [newQuestions]
  );
  const highlightedQuestions = useHighlightOnNewItems(newQuestionTexts);

  const handleCopyQuestion = (index: number) => {
    const newQuestions = [...questions];
    newQuestions[index].copied = true;
    setQuestions(newQuestions);
    setTimeout(() => {
      const newQuestions = [...questions];
      newQuestions[index].copied = false;
      setQuestions(newQuestions);
    }, 3000);
  };

  const handleSelectQuestion = (index: number) => {
    const newQuestions = [...questions];
    newQuestions[index].selected = !newQuestions[index].selected;
    setQuestions(newQuestions);
  };

  const renderQuestionStatus = (item: arrayQuestion, index: number) => {
    switch (item.status) {
      case 'loading':
        return <CircularProgress size={18} color="inherit" />;
      case 'error':
        return <WarningIcon color="inherit" />;
      case 'sent':
        return <CheckCircleIcon sx={{ color: '#8CCF95' }} />;
      default:
        return (
          <Checkbox
            sx={{ ...classes.checkbox, '&.Mui-checked': { color: '#2D388D !important' } }}
            checked={item.selected}
            onChange={() => handleSelectQuestion(index)}
          />
        );
    }
  };

  useEffect(() => {
    if (hasFeedback && !isLoading && highlightedQuestions.length > 0) {
      // Add a small delay to ensure DOM is updated with highlighted elements
      setTimeout(() => {
        scrollToHighlighted();
      }, 100);
    }
  }, [hasFeedback, isLoading, scrollToHighlighted, highlightedQuestions, newQuestionTexts]);

  return (
    <Box sx={{ ...classes.questionsContainer, ...(maxHeight && { maxHeight }) }} ref={containerRef}>
      {questions &&
        questions.map((item, index) => {
          const isHighlighted = highlightedQuestions.includes(item.text);
          return (
            <Box
              sx={{
                ...classes.question,
                justifyContent: 'space-around',
                ...(isHighlighted && {
                  ...classes.questionActive
                })
              }}
              key={item.id}
              data-highlighted={isHighlighted}
            >
              <Box sx={classes.checkboxContainer}>{renderQuestionStatus(item, index)}</Box>
              <Box sx={{ maxWidth: '700px' }}>{item.text}</Box>
              {deleteQuestion ? (
                <Box>
                  <CopyToClipboard text={item.text} onCopy={() => handleCopyQuestion(index)}>
                    <Tooltip placement="top" title={item.copied ? 'Copied!' : 'Copy'} arrow>
                      <IconButton sx={{ color: '#6C82EF' }}>
                        {item.copied ? <DoneIcon /> : <FileCopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </CopyToClipboard>
                  <Tooltip placement="top" title={'Delete'} arrow>
                    <IconButton sx={{ color: '#D6827D' }} onClick={() => deleteQuestion(index)}>
                      {item.status === 'deleting' ? <CircularProgress size={24} /> : <DeleteIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <CopyToClipboard text={item.text} onCopy={() => handleCopyQuestion(index)}>
                  <Button
                    className="copy-question-button"
                    sx={classes.copyButton}
                    endIcon={item.copied ? <DoneIcon /> : <FileCopyIcon />}
                  >
                    {item.copied ? 'Copied!' : 'Copy'}
                  </Button>
                </CopyToClipboard>
              )}
            </Box>
          );
        })}
    </Box>
  );
};

export default SelectableCopyableQuestions;
